import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

// Interfaces
import { ILocation } from "@/Common/interfaces/location";
import { IPreferences, IUser } from "@/Common/interfaces/user";

// API's
import { retrieveLocationWithUserPermissions } from "@/Common/api/location";

// Custom Hooks
import { useWrapperLayer } from "@/Common/context/wrapperLayer/WrapperLayerContext";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useLocations } from "@/Common/hooks/Location/useLocations";
import { useQueryParams } from "@/Common/hooks/Query/useQueryParams";

// Store Redux
import { actions as actionsLocation } from "@/redux/reducers/Location";
import { actions as userActions } from "@/redux/reducers/User";
import { RootState } from "@/redux/store";

// Components
import { LocationSelectorButton } from "./LocationSelectorButton";
import { LocationSelectorDropdown } from "./LocationSelectorDropdown";

// Constants
import { tenant } from "@/Constants/globalConfig";
import { Dispatch } from "@reduxjs/toolkit";

const getUserLocationPreference = (preferences: IPreferences | undefined) => {
	if (!preferences?.landingLocation) {
		return null;
	}
	return preferences?.landingLocation;
};

const dispatchLocation = (dispatch: Dispatch, newLocation?: ILocation | null) => {
	if (!newLocation) {
		return;
	}
	dispatch(actionsLocation.SET_LOCATION({ location: newLocation }));
};

const dispatchUser = (dispatch: Dispatch, queryClient: QueryClient, newUser?: IUser | null) => {
	if (!newUser) {
		return;
	}
	queryClient.setQueryData(["loggedUser"], newUser);
	dispatch(userActions.SET_USER({ user: newUser }));
};

const LOCATION_PATH = "loc";

const LocationSelector = () => {
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();

	const { isDisabled } = useWrapperLayer();
	const user = useSelector((state: RootState) => state.user.user);
	const { location, locationId } = useSelector((state: RootState) => state.location);
	const { locations, isLoading: isLoadingLocations } = useLocations(tenant);
	const { location: queryLocation } = useQueryParams();

	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const { usePresentByPattern } = useAuthorization();
	const isAuthorized = usePresentByPattern("ACCESS_LOCATION_", 2);

	const open = Boolean(anchorEl);
	const dispatch = useDispatch();

	const [previousSelectedLocationId, setPreviousSelectedLocationId] = useState(location?.locationId);

	const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
		(event) => {
			setAnchorEl(event.currentTarget);
		},
		[setAnchorEl]
	);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const handleMenuClick = useCallback(
		async (selectedId: ILocation["locationId"]) => {
			setAnchorEl(null);
			dispatch(actionsLocation.SET_LOCATION_ID({ locationId: selectedId }));

			const queryParams = new URLSearchParams(searchParams);
			if (queryParams.size < 2) {
				setSearchParams({});
			} else {
				const selectedLocation = locations?.find((loc) => loc.locationId === selectedId);
				if (selectedLocation) {
					queryParams.set(LOCATION_PATH, selectedLocation.prefix);
				} else {
					queryParams.delete(LOCATION_PATH);
				}
				setSearchParams(queryParams);
			}
		},
		[searchParams, setSearchParams, dispatch, locations]
	);

	const { data: locationWithUserAccess, isFetching } = useQuery({
		queryKey: ["locationWithUserPermissions", locationId],
		placeholderData: {
			location,
			user,
		},
		enabled: !!user?._id && !isLoadingLocations && !!locationId && location?.locationId !== locationId,
		queryFn: async () => {
			const data = await retrieveLocationWithUserPermissions(locationId, user?._id);

			if (!data?.location || !data?.user) {
				dispatch(actionsLocation.SET_LOCATION_ID({ locationId: previousSelectedLocationId ?? "" }));
				return { location, user };
			}

			return data;
		},
	});

	useEffect(() => {
		dispatchLocation(dispatch, locationWithUserAccess?.location);
		dispatchUser(dispatch, queryClient, locationWithUserAccess?.user);
		setPreviousSelectedLocationId(locationId);
	}, [locationWithUserAccess]);

	useEffect(() => {
		if (locationId && !queryLocation) {
			return;
		}

		if (queryLocation) {
			const queryLocationId = queryLocation.locationId;
			setPreviousSelectedLocationId(queryLocationId);
			dispatch(actionsLocation.SET_LOCATION_ID({ locationId: queryLocationId }));
			return;
		}

		if (user) {
			const preferedLocationId = getUserLocationPreference(user?.preferences) ?? user?.locationId;
			if (preferedLocationId) {
				setPreviousSelectedLocationId(preferedLocationId);
				dispatch(actionsLocation.SET_LOCATION_ID({ locationId: preferedLocationId }));
			}
		}
	}, [queryLocation, user, dispatch]);

	useEffect(() => {
		dispatch(actionsLocation.SET_LOADING({ loading: isFetching }));
	}, [isFetching]);

	return (
		<>
			<LocationSelectorButton
				open={open}
				disabled={!isAuthorized || isFetching || isDisabled || !locations?.length}
				actionable={isAuthorized}
				label={location?.name}
				handleClick={handleClick}
			/>
			<LocationSelectorDropdown
				anchorEl={anchorEl}
				open={open}
				handleClose={handleClose}
				locations={locations ?? []}
				handleMenuClick={handleMenuClick}
				location={location}
			/>
		</>
	);
};

export default LocationSelector;
